import * as React from 'react';
import { format } from 'date-fns';
import styled from 'styled-components';
import { ClickAwayListener } from '@material-ui/core';
import { ReactDatePickerProps } from 'react-datepicker';

import EventNoteOutlinedIcon from '@material-ui/icons/EventNoteOutlined';

import { DatePicker } from '../atoms';
import { DEFAULT_DATE_FORMAT } from '../constants';

const DatePickerRange = ({
  initialDates = [null, null],
  onRangeChange,
  hasError,
  isRequired,
  placeholderText = 'Select Date',
  ...props
}: Props) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const [startDate, setStartDate] = React.useState(
    initialDates[0],
  );
  const [endDate, setEndDate] = React.useState(
    initialDates[1],
  );

  const selectDateMessage = <span>{placeholderText}</span>;

  const range = isOpen ? (
    selectDateMessage
  ) : (
    <>
      <span>
        {startDate &&
          format(startDate, DEFAULT_DATE_FORMAT)}
      </span>
      <span>→</span>
      <span>
        {endDate && format(endDate, DEFAULT_DATE_FORMAT)}
      </span>
    </>
  );

  const handleOnChange = dates => {
    const [start, end] = dates;

    setStartDate(start);
    setEndDate(end);

    if (onRangeChange) {
      onRangeChange(start, end);
    }
  };

  React.useEffect(() => {
    if (endDate && isOpen) {
      setIsOpen(false);
    }
  }, [endDate]);

  return (
    <ClickAwayListener onClickAway={() => setIsOpen(false)}>
      <Container
        onClick={() => setIsOpen(true)}
        hasError={hasError}
      >
        <DataContainer>
          <EventNoteOutlinedIcon />
          {startDate && endDate ? range : selectDateMessage}
        </DataContainer>
        <DatePicker
          open={isOpen}
          selectsRange
          endDate={endDate}
          selected={startDate}
          startDate={startDate}
          required={isRequired}
          showPopperArrow={false}
          onChange={handleOnChange}
          placeholderText={placeholderText}
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...props}
        />
      </Container>
    </ClickAwayListener>
  );
};

const Container = styled.div.attrs({
  className:
    'MuiFormControl-root MuiTextField-root WithStyles(ForwardRef(TextField))-root-1',
})<{ hasError: boolean }>`
  position: relative;
  min-width: 40px;
  border-radius: 8px;
  border: 1px solid
    ${({ hasError }) =>
      hasError ? 'var(--danger-500)' : 'var(--border)'};
  background: var(--neutral-0);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: ${({ hasError }) =>
      hasError ? 'var(--danger-500)' : 'var(--neutral-400)'};
  }

  &:focus-within {
    border-color: ${({ hasError }) =>
      hasError ? 'var(--danger-500)' : 'var(--primary-600)'};
    box-shadow: var(--shadow-focus);
  }

  .react-datepicker-wrapper {
    display: none;
  }

  .react-datepicker-popper {
    top: 40px !important;
    left: -51px !important;
    transform: none !important;
  }
`;

const DataContainer = styled.div.attrs({
  className:
    'MuiInputBase-root MuiOutlinedInput-root MuiInputBase-formControl',
})`
  display: flex;
  height: 40px;
  padding: 0 14px;
  cursor: pointer;
  font-family: 'Segoe UI', -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, Roboto, sans-serif;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mainText);
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 8px;

  > svg {
    font-size: 1.125rem;
    margin: 0 8px 0 0;
    color: var(--neutral-500);
  }

  > span {
    letter-spacing: 0.01em;

    &:nth-of-type(2) {
      padding: 0 8px;
      color: var(--neutral-500);
    }
  }
`;

export type Props = Omit<
  ReactDatePickerProps,
  'onChange'
> & {
  isRequired?: boolean;
  initialDates?: Date[];
  hasError?: boolean;
  onChange?: (
    date: Date | [Date, Date] | null,
    event: React.SyntheticEvent<any> | undefined,
  ) => void;
  onRangeChange?: (start: Date, end: Date) => void;
};

export default DatePickerRange;
